import React, {useEffect, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'

import {
    CommandBarButton,
    DefaultButton,
    Dropdown,
    IBasePickerSuggestionsProps,
    IComboBoxOption,
    IconButton,
    IDropdownOption,
    IPersonaProps,
    Modal,
    PrimaryButton,
    Separator,
    Shimmer,
    Stack,
    StackItem,
    Text
} from "@fluentui/react"
import {ApprovalEmailPreview, ApprovalLookup, contentStyles, iconButtonStyles, ILookupValues} from "./"
import {useBranding} from "../../hooks/useBranding"
import ApprovalService, {
    IGetApprovalActionResponse, IGetApprovalListMasterResponse,
    IGetApprovalStepResponse,
    IPostApprovalActionRequest,
    IPutApprovalActionRequest
} from "../../services/Approval"
import {RHFTextBoxControl} from "../RHFControls/RHFTextBoxControl"
import {Controller, FormProvider, useForm} from 'react-hook-form'
import StatusMessage, {StatusEnum} from "../../share/StatusMessage"
import RichTextEditor, {EditorValue} from 'react-rte'
import {SingleRHFRichTextControl} from '../RHFControls/SingleRHFRichTextControl'
import {toolbarConfigEmailBody, toolbarConfigEmailSubject} from '../EmailTemplate'
import {removeHTMLTags} from "../RenewalsAndAlerts/RenewalsAndAlerts.data";
import {RHFPeoplePickerControl} from "../RHFControls/RHFPeoplePickerControl";
import FormTemplateLinkService from "../TemplateLink/FormTemplateLink.service";
import {RHFCheckBoxControl} from "../RHFControls/RHFChekBoxControl";


type Properties = {
    action: IGetApprovalActionResponse | null
    actions: IGetApprovalActionResponse[]
    currentStep: IGetApprovalStepResponse | null
    documentOnlyAudienceId: string
    onDismissModal: (update: boolean) => void
    showModal: boolean
    templateId: string
    templateContentControls: IComboBoxOption[]
    mapContentControls: IComboBoxOption[]
    masterLists: IGetApprovalListMasterResponse[]
}

export function ApprovalActionEdit({
                                       action,
                                       actions,
                                       currentStep,
                                       documentOnlyAudienceId,
                                       mapContentControls,
                                       masterLists,
                                       onDismissModal,
                                       showModal,
                                       templateContentControls,
                                       templateId
                                   }: Properties) {

    const {t} = useTranslation(['approval', 'common'])
    const myForm = useForm()
    const {branding} = useBranding()

    const [currentAction, setCurrentAction] = useState<IGetApprovalActionResponse | null>(null)
    const [isErrorOnLoad, setIsErrorOnLoad] = useState(false)
    const [isLoadedData, setIsLoadedData] = useState(false)
    const [isLoadingData, setIsLoadingData] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [message, setMessage] = useState("")
    const [peopleList, setPeopleList] = useState<IPersonaProps[]>([])
    const [rteValue, setRteValue] = useState(RichTextEditor.createEmptyValue())
    const [rteValueSubject, setRteValueSubject] = useState(RichTextEditor.createEmptyValue())
    const [selectedPeopleList1, setSelectedPeopleList1] = useState<IPersonaProps[]>([])
    const [selectedPeopleList2, setSelectedPeopleList2] = useState<IPersonaProps[]>([])
    const [showEmailPreview, setShowEmailPreview] = useState(false)
    const [showLookup, setShowLookup] = useState(false)
    const [showMessage, setShowMessage] = useState(false)
    const [statusMessageEnum, setStatusMessageEnum] = useState<StatusEnum>(StatusEnum.Info)

    const suggestionProps: IBasePickerSuggestionsProps = {
        mostRecentlyUsedHeaderText: t('mostRecentlyUsedHeaderText', {ns: 'emailtemplate'}),
        suggestionsContainerAriaLabel: t('suggestionsContainerAriaLabel', {ns: 'emailtemplate'}),
        suggestionsHeaderText: t('mostRecentlyUsedHeaderText', {ns: 'emailtemplate'}),
    }

    const watchedType = myForm.watch("Type")

    const typeOptions: IComboBoxOption[] = [
        {
            key: 'Approve',
            text: 'Approve'
        },
        {
            key: 'Reject',
            text: 'Reject'
        },
        {
            key: 'Complete',
            text: 'Complete'
        },
        {
            key: 'Abort',
            text: 'Abort'
        },
    ]

    useEffect(() => {
        if (showModal) {
            onLoad().then()
        }
    }, [showModal])


    /**
     * A function to display a custom error message and optionally control a retry status flag.
     *
     * @param {any} error - The error object or message to be displayed. If the error has an `error` property,
     *                      it will be used as part of the displayed message; otherwise, the full error is converted to a string.
     * @param {boolean} [showTryAgain=false] - Optional flag indicating whether to enable retry behavior.
     *                                         When set to true, it updates the retry status flag.
     *
     * This function uses a localized error message template and injects the formatted error into it.
     * Additionally, it triggers a status message of type `Error` and, when applicable, sets a retry indicator.
     */
    const setCustomError = (error: any, showTryAgain: boolean = false) => {
        showStatusMessage(t('message.error', {
            ns: 'common',
            Error: error?.error ? error.error.toString() : error.toString()
        }), StatusEnum.Error)
        if (showTryAgain) setIsErrorOnLoad(true)
    }

    /**
     * show Status Message
     * @param message
     * @param status
     */
    const showStatusMessage = (message: string, status: StatusEnum) => {
        setMessage(message)
        setStatusMessageEnum(status)
        setShowMessage(true)
    }

    /**
     * On Before dismiss
     */
    const onBeforeDismissAction = () => {
        onDismissModal(false)
    }

    const onLoad = async () => {

        await initLoad().then()
    }

    /**
     * Load init
     */
    const initLoad = async () => {
        setMessage("")
        setShowMessage(false)
        setIsErrorOnLoad(false)
        setIsLoadingData(true)
        setIsLoadedData(false)
        setIsSaving(false)
        setPeopleList([])
        setSelectedPeopleList1([])
        setSelectedPeopleList2([])
        setRteValueSubject(EditorValue.createFromString("", "html"))
        setRteValue(EditorValue.createFromString("", "html"))

        myForm.clearErrors()
        myForm.reset()

        if (action !== null) {

            myForm.setValue("Name", action?.Name)
            myForm.setValue("EmailSubject", action?.EmailSubject)
            myForm.setValue("EmailTemplate", action?.EmailTemplate)
            myForm.setValue("Type", action?.Type)
            myForm.setValue("MapControls", action?.MapControls)
            myForm.setValue("EmailAddress", action?.EmailAddress)
            myForm.setValue("AdditionalNotifys", action?.AdditionalNotifys)
            myForm.setValue("UseManagerAttribute", action?.UseManagerAttribute ?? false)

            try {
                const lookupRuleArray = JSON.parse(action?.EmailAddressLookupRule) as ILookupValues[]
                const currentList = lookupRuleArray.find(item => item.Name.toLowerCase().trim() === "looklist")

                if (currentList) {

                    const currentListItem = masterLists.find(item => item.Id  === Number(currentList.Value))

                    if (currentListItem) {
                        myForm.setValue("EmailAddressLookupRule", action?.EmailAddressLookupRule)
                    } else {
                        action.EmailAddressLookupRule = ''
                        myForm.setValue("EmailAddressLookupRule", "")
                    }
                }

            } catch (_) {

            }


            setSelectedPeopleList1(action?.EmailAddress !== "" ? action?.EmailAddress.split(",").map(item => {
                return {
                    text: item,
                    secondaryText: item
                }
            }) : [])
            setSelectedPeopleList2(action?.AdditionalNotifys !== "" ? action?.AdditionalNotifys.split(",").map(item => {
                return {
                    text: item,
                    secondaryText: item
                }
            }) : [])

            setRteValue(EditorValue.createFromString(action?.EmailTemplate, 'html'))
            setRteValueSubject(EditorValue.createFromString(action?.EmailSubject, 'html'))

        }

        if (actions.length > 0) {
            myForm.setValue("MapControls", actions[0].MapControls)
        }

        await FormTemplateLinkService.GetDocumentOnlyAudienceDetailByAudienceMasterId({
            AvoidCache: true,
            AudienceMasterId: documentOnlyAudienceId
        })
            .then(async (data) => {
                if (data.length > 0) {

                    const people: IPersonaProps[] = data.map<IPersonaProps>(item => {
                        return {
                            text: item.FullName?.trim().length === 0 ? item.UserName : item.FullName,
                            secondaryText: item.UserName
                        }
                    })
                    setPeopleList(people)
                    setIsLoadedData(true)
                }
            })
            .catch(error => {
                setCustomError(error, true)
            })
            .finally(() => {
                setIsLoadingData(false)
            })
    }

    /**
     * Retrieves a filtered list of type options by excluding any options
     * that have matching types in the actions list.
     *
     * @return {Array} Returns an array of filtered type options where no type matches an action type.
     */
    function GetTypeOptions(): Array<IComboBoxOption> {

        if (action !== null) {
            return typeOptions.filter(option => !actions.some(action => action.Type === option.key) || action.Type === option.key)
        } else {
            return typeOptions.filter(option => !actions.some(action => action.Type === option.key))
        }
    }

    const onUpdate = async () => {
        setIsSaving(false)
        setShowMessage(false)
        myForm.clearErrors()

        await myForm.handleSubmit(async (_) => {

                const subject = removeHTMLTags(rteValueSubject.toString("html"))
                const body = removeHTMLTags(rteValue.toString("html"))
                if (subject.trim().length === 0 || body.trim().length === 0) {
                    if (subject.trim().length === 0) myForm.setError("EmailSubject", {message: t('text.fieldRequired', {ns: 'common'})})
                    if (body.trim().length === 0) myForm.setError("EmailTemplate", {message: t('text.fieldRequired', {ns: 'common'})})
                    showStatusMessage(t("message.fieldsRequired", {ns: "common"}), StatusEnum.Error)
                } else {
                    setIsSaving(true)

                    let toAdditionalNotifys: string | undefined = selectedPeopleList2.map(item => item.secondaryText).join(", ")
                    let to: string | undefined = selectedPeopleList1.map(item => item.secondaryText).join(", ")

                    const request: IPutApprovalActionRequest = {
                        Id: action?.Id ?? 0,
                        AdditionalNotifys: toAdditionalNotifys,
                        ApprovalStepId: currentStep?.Id ?? 0,
                        EmailAddress: watchedType === 'Approve' ? to : '',
                        EmailAddressLookupRule: watchedType === 'Approve' ? myForm.getValues("EmailAddressLookupRule") : '',
                        EmailSubject: subject,
                        EmailTemplate: rteValue.toString("html"),
                        MapControls: myForm.getValues("MapControls"),
                        Name: myForm.getValues("Name"),
                        TemplateId: templateId,
                        UseManagerAttribute: watchedType !== 'Approve' || myForm.getValues("UseManagerAttribute") === undefined ? false : myForm.getValues("UseManagerAttribute"),
                        Type: myForm.getValues("Type")
                    }

                    await ApprovalService.putApprovalAction(request)
                        .then(data => {
                            if (data.Success) {
                                showStatusMessage(t('message.successUpdated', {ns: 'common'}), StatusEnum.Success)

                                onDismissModal(true)

                            } else {
                                showStatusMessage(t('message.error', {
                                    ns: 'common',
                                    Error: data.ExceptionMessage
                                }), StatusEnum.Error)
                            }
                        })
                        .catch((error) => {
                            showStatusMessage(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
                        })
                        .finally(() => {
                            setIsSaving(false)
                        })
                }


            }, () => {
                showStatusMessage(t("message.fieldsRequired", {ns: "common"}), StatusEnum.Error)
            }
        )()

    }


    const onSave = async () => {
        setIsSaving(false)
        setShowMessage(false)
        myForm.clearErrors()

        await myForm.handleSubmit(async (_) => {

                const subject = removeHTMLTags(rteValueSubject.toString("html"))
                const body = removeHTMLTags(rteValue.toString("html"))
                if (subject.trim().length === 0 || body.trim().length === 0) {
                    if (subject.trim().length === 0) myForm.setError("EmailSubject", {message: t('text.fieldRequired', {ns: 'common'})})
                    if (body.trim().length === 0) myForm.setError("EmailTemplate", {message: t('text.fieldRequired', {ns: 'common'})})
                    showStatusMessage(t("message.fieldsRequired", {ns: "common"}), StatusEnum.Error)
                } else {
                    setIsSaving(true)

                    let toAdditionalNotifys: string | undefined = selectedPeopleList2.map(item => item.secondaryText).join(", ")
                    let to: string | undefined = selectedPeopleList1.map(item => item.secondaryText).join(", ")

                    const request: IPostApprovalActionRequest = {
                        AdditionalNotifys: toAdditionalNotifys,
                        ApprovalStepId: currentStep?.Id ?? 0,
                        EmailAddress: watchedType === 'Approve' ? to : '',
                        EmailAddressLookupRule: watchedType === 'Approve' ? myForm.getValues("EmailAddressLookupRule") : '',
                        EmailSubject: subject,
                        EmailTemplate: rteValue.toString("html"),
                        MapControls: myForm.getValues("MapControls"),
                        Name: myForm.getValues("Name"),
                        TemplateId: templateId,
                        Type: myForm.getValues("Type"),
                        UseManagerAttribute: watchedType !== 'Approve' || myForm.getValues("UseManagerAttribute") === undefined ? false : myForm.getValues("UseManagerAttribute"),
                        Mode: 'create'
                    }

                    await ApprovalService.postApprovalAction(request)
                        .then(data => {
                            if (data.Success) {
                                showStatusMessage(t('message.successCreated', {ns: 'common'}), StatusEnum.Success)

                                onDismissModal(true)

                            } else {
                                showStatusMessage(t('message.error', {
                                    ns: 'common',
                                    Error: data.ExceptionMessage
                                }), StatusEnum.Error)
                            }
                        })
                        .catch((error) => {
                            showStatusMessage(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
                        })
                        .finally(() => {
                            setIsSaving(false)
                        })
                }


            }, () => {
                showStatusMessage(t("message.fieldsRequired", {ns: "common"}), StatusEnum.Error)
            }
        )()

    }


    /**
     * Callback function to handle the dismissal of the email preview.
     * It resets the current action state and hides the email preview modal.
     */
    const onDismissPreviewEmail = () => {
        setCurrentAction(null)
        setShowEmailPreview(false)
    }

    /**
     * A callback function triggered to dismiss the lookup component.
     *
     * @param {boolean} [update=false] - Determines whether an update action should occur upon dismissing the lookup. Defaults to false.
     * @param {string} [value] - An optional value associated with the dismiss action. Defaults to undefined if not provided.
     */
    const onDismissLookup = (update: boolean = false, value?: string) => {
        if (update) {

            if (action) {
                action.EmailAddressLookupRule = value ?? ''
            }

            myForm.setValue("EmailAddressLookupRule", value ?? '')
        }
        setShowLookup(false)
    }


    /**
     * Function onRenderOption for DropdownList of customControls
     * @param option
     */
    const onRenderOption = (option?: IDropdownOption): JSX.Element => {
        return (
            <span>{option!.text}</span>
        )
    }


    /**
     * Processes and prepares the necessary data for previewing an email, then updates the state to show the email preview.
     *
     * @return {void} This method does not return a value as its primary function is to update the state with the email preview data.
     */
    function ShowPreview(): void {

        let toAdditionalNotifys: string | undefined = selectedPeopleList2.map(item => item.secondaryText).join(", ")
        let to: string | undefined = selectedPeopleList1.map(item => item.secondaryText).join(", ")

        const tempAction: IGetApprovalActionResponse = {
            AdditionalNotifys: toAdditionalNotifys ?? '',
            ApprovalStepId: 0,
            DateCreated: "",
            DateUpdated: "",
            EmailAddress: to ?? '',
            EmailAddressLookupRule: myForm.getValues("EmailAddressLookupRule"),
            Id: 0,
            MapControls: myForm.getValues("MapControls"),
            Name: "",
            Type: myForm.getValues("Type"),
            EmailSubject: removeHTMLTags(rteValueSubject.toString("html")),
            EmailTemplate: rteValue.toString("html"),
            UseManagerAttribute: false
        }

        setCurrentAction(tempAction)
        setShowEmailPreview(true)
    }

    return (
        <>
            <Modal
                isOpen={showModal}
                isBlocking={true}
                containerClassName={contentStyles(branding.theme).containerRecordPreview}
            >

                <Stack grow={1} styles={{root: {height: "100%"}}}>
                    <Stack tokens={{childrenGap: 8}} styles={{
                        root: {
                            height: 56,
                            alignItems: 'center'
                        }
                    }} horizontal>

                        <Stack grow={1}
                               styles={{
                                   root: {
                                       textOverflow: "ellipsis",
                                       paddingLeft: 16,
                                       whiteSpace: "nowrap",
                                       overflow: "hidden"
                                   }
                               }}>
                            <Text variant={"xLarge"} id={'approvalActionEditTitle'}
                                  title={action === null ? t('title.newApprovalAction', {ns: 'approval'}) : t('title.editApprovalAction', {
                                      ns: 'approval',
                                      Name: action?.Type
                                  })}>
                                {action === null ? t('title.newApprovalAction', {ns: 'approval'}) : t('title.editApprovalAction', {
                                    ns: 'approval',
                                    Name: action?.Type
                                })}
                            </Text>
                        </Stack>

                        <Stack horizontal styles={{root: {alignItems: "center", paddingRight: 8}}}>
                            <IconButton
                                styles={iconButtonStyles}
                                iconProps={{iconName: 'Cancel'}}
                                ariaLabel={t('Common.Button.Close', {ns: 'common'})}
                                onClick={onBeforeDismissAction}
                                title={t('Common.Button.Close', {ns: 'common'})}
                            />
                        </Stack>
                    </Stack>
                    <Stack grow={1} styles={{root: {height: 1}}}>

                        <Stack horizontal styles={{root: {height: '100%'}}}>

                            <Stack grow={1} styles={{root: {height: '100%', width: 1}}}>

                                {/*-- START CONTENT --*/}
                                <Stack grow={1} styles={{root: {overflowY: 'auto', paddingLeft: 16, paddingRight: 16}}}
                                       className="scrollVisible">

                                    {/*region MESSAGES*/}
                                    <StackItem styles={{root: {paddingBottom: 16}}}>
                                        {showMessage &&
                                            <>
                                                <StatusMessage status={statusMessageEnum} hasTimer={false}
                                                               truncated={true}
                                                               isMultiline={true}
                                                               dismiss={true}
                                                               setShowMessage={setShowMessage}>
                                                    <div>{message}</div>
                                                </StatusMessage>
                                            </>
                                        }
                                    </StackItem>
                                    {/*endregion*/}


                                    {/*region TRY AGAIN*/}
                                    {isErrorOnLoad &&
                                        <>
                                            <Stack grow={1} styles={{
                                                root: {
                                                    height: "100%",
                                                    justifyContent: "center",
                                                    alignItems: "center"
                                                }
                                            }}>
                                                <StackItem>
                                                    <DefaultButton onClick={() => {
                                                        onLoad().then()
                                                    }} style={{marginLeft: 8}}
                                                                   text={t('Common.Button.Try', {ns: 'common'})}
                                                                   title={t('Common.Button.Try', {ns: 'common'})}/>
                                                </StackItem>
                                                <StackItem tokens={{padding: 8}}>
                                                    <Text
                                                        variant={"medium"}>{t("text.errorAndTry", {ns: "common"})}</Text>
                                                </StackItem>
                                            </Stack>
                                        </>
                                    }
                                    {/*endregion*/}

                                    {/*region CONTENT FORM*/}
                                    {!isErrorOnLoad &&

                                        <>

                                            <FormProvider {...myForm}>
                                                <form>
                                                    {isLoadingData && <>
                                                        <Shimmer styles={{root: {marginTop: 16}}}/>
                                                        <Shimmer styles={{root: {marginTop: 16}}}/>
                                                        <Shimmer styles={{root: {marginTop: 16}}}/>
                                                        <Shimmer styles={{root: {marginTop: 32}}}/>
                                                        <Shimmer styles={{root: {marginTop: 16}}}/>
                                                        <Shimmer styles={{root: {marginTop: 16}}}/>
                                                        <Shimmer styles={{root: {marginTop: 16}}}/>
                                                    </>}
                                                    {isLoadedData &&
                                                        <>
                                                            <Text variant={"medium"} block
                                                                  styles={{root: {paddingBottom: 4}}}>
                                                                <Trans>
                                                                    {t("text.instructionAction", {ns: "approval"})}
                                                                </Trans>
                                                            </Text>

                                                            <RHFTextBoxControl
                                                                id={"Name"}
                                                                label={t('label.ctrlName', {ns: 'common'})}

                                                                rules={{
                                                                    required: t('text.fieldRequired', {ns: 'common'}),
                                                                    validate: {
                                                                        required:
                                                                            (value) => {
                                                                                return !!value.trim() || t('text.fieldRequired', {ns: 'common'})
                                                                            }
                                                                    },
                                                                }}

                                                                placeholder={t('label.ctrlName', {ns: 'common'})}
                                                                title={t('label.ctrlName', {ns: 'common'})}
                                                                ariaLabel={t('label.ctrlName', {ns: 'common'})}
                                                                maxLength={50}
                                                                control={myForm.control}
                                                                setValue={myForm.getValues}
                                                                getValues={myForm.getValues}
                                                                trigger={myForm.trigger}
                                                                required
                                                                disabled={isSaving}
                                                            >
                                                            </RHFTextBoxControl>

                                                            <Stack horizontal wrap={true} tokens={{childrenGap: 8}}>
                                                                <Stack grow={1}>
                                                                    <Controller name="Type" control={myForm.control}

                                                                                rules={{required: t('text.fieldRequired', {ns: 'common'})}}
                                                                                render={({
                                                                                             field,
                                                                                             fieldState: {error}
                                                                                         }) =>
                                                                                    <Dropdown selectedKey={field.value}
                                                                                              {...field}
                                                                                              required={true}
                                                                                              onChange={(_, option) => {
                                                                                                  field.onChange(option?.key)
                                                                                              }}
                                                                                              errorMessage={error && error.message}
                                                                                              options={GetTypeOptions()}
                                                                                              label={t('label.ctrlType', {ns: 'common'})}
                                                                                              disabled={isSaving || action !== null}
                                                                                              title={t('text.selectOption', {ns: 'common'})}

                                                                                    />}
                                                                    />
                                                                </Stack>

                                                                <Stack grow={1}>
                                                                    <Controller name="MapControls"
                                                                                control={myForm.control}
                                                                                rules={{required: t('text.fieldRequired', {ns: 'common'})}}
                                                                                render={({
                                                                                             field,
                                                                                             fieldState: {error}
                                                                                         }) =>
                                                                                    <Dropdown selectedKey={field.value}
                                                                                              {...field}
                                                                                              required={true}
                                                                                              onChange={(_, option) => {
                                                                                                  field.onChange(option?.key)
                                                                                              }}
                                                                                              errorMessage={error && error.message}
                                                                                              options={mapContentControls}
                                                                                              label={t('title.mapControl', {ns: 'approval'})}
                                                                                              disabled={isSaving || actions.length > 0}
                                                                                              title={t('title.mapControl', {ns: 'approval'})}

                                                                                    />}
                                                                    />
                                                                </Stack>

                                                            </Stack>


                                                            <Separator
                                                                styles={{root: {marginTop: 16}}}>{t('title.notificationEmail', {ns: 'common'})}
                                                            </Separator>


                                                            {watchedType === 'Approve' && <>


                                                                <RHFPeoplePickerControl
                                                                    required
                                                                    readOnly={isSaving}
                                                                    label={t('text.email', {ns: 'approval'})}
                                                                    pickerSuggestionsProps={suggestionProps}
                                                                    control={myForm.control}
                                                                    setValue={myForm.setValue}
                                                                    getValues={myForm.getValues}
                                                                    trigger={myForm.trigger}
                                                                    itemLimit={1}
                                                                    id={"EmailAddress"}
                                                                    rules={{required: t('text.fieldRequired', {ns: 'common'})}}
                                                                    onCallback={(personas: IPersonaProps[]) => {
                                                                        setSelectedPeopleList1(personas)
                                                                    }}
                                                                    peopleList={peopleList}
                                                                    selectedPeopleList={selectedPeopleList1}
                                                                    setSelectedPeopleList={setSelectedPeopleList1}
                                                                />

                                                                <Stack horizontal wrap={true}
                                                                       tokens={{childrenGap: 8}}
                                                                       styles={{
                                                                           root: {
                                                                               marginTop: 8,
                                                                               marginBottom: 8,

                                                                           }
                                                                       }}>

                                                                    <Stack grow={1}>


                                                                        <RHFTextBoxControl
                                                                            id={"EmailAddressLookupRule"}
                                                                            label={t('title.lookupRule', {ns: 'approval'})}
                                                                            title={myForm.getValues("EmailAddressLookupRule") ?? t('title.lookupRule', {ns: 'approval'})}
                                                                            ariaLabel={t('title.lookupRule', {ns: 'approval'})}
                                                                            control={myForm.control}
                                                                            setValue={myForm.getValues}
                                                                            getValues={myForm.getValues}
                                                                            trigger={myForm.trigger}
                                                                            disabled={true}
                                                                        />


                                                                    </Stack>
                                                                    <Stack styles={{
                                                                        root: {
                                                                            paddingTop: 28
                                                                        }
                                                                    }}>
                                                                        <PrimaryButton disabled={isSaving}
                                                                                       onClick={() => {
                                                                                           setShowLookup(true)
                                                                                       }}
                                                                                       text={t('Common.Button.Configure', {ns: 'common'})}
                                                                                       title={t('Common.Button.Configure', {ns: 'common'})}/>
                                                                    </Stack>

                                                                </Stack>

                                                                <Stack styles={{
                                                                    root: {
                                                                        paddingTop: 8,
                                                                        marginBottom: 8
                                                                    }
                                                                }}>
                                                                    <RHFCheckBoxControl
                                                                        id={"UseManagerAttribute"}
                                                                        label={t('title.managerAttribute', {ns: 'approval'})}
                                                                        title={t('title.managerAttribute', {ns: 'approval'})}
                                                                        control={myForm.control}
                                                                        setValue={myForm.setValue}
                                                                        getValues={myForm.getValues}
                                                                        trigger={myForm.trigger}
                                                                        disabled={isSaving}
                                                                    />
                                                                </Stack>
                                                                <Separator/>
                                                            </>
                                                            }

                                                            <RHFPeoplePickerControl
                                                                readOnly={isSaving}
                                                                label={t('title.additionalEmails', {ns: 'approval'})}
                                                                pickerSuggestionsProps={suggestionProps}
                                                                control={myForm.control}
                                                                setValue={myForm.setValue}
                                                                getValues={myForm.getValues}
                                                                trigger={myForm.trigger}
                                                                id={"AdditionalNotifys"}
                                                                onCallback={(personas: IPersonaProps[]) => {
                                                                    setSelectedPeopleList2(personas)
                                                                }}
                                                                peopleList={peopleList}
                                                                selectedPeopleList={selectedPeopleList2}
                                                                setSelectedPeopleList={setSelectedPeopleList2}
                                                            />


                                                            <StackItem styles={{root: {paddingTop: 24}}}>

                                                                <CommandBarButton onClick={() => {
                                                                    ShowPreview()
                                                                }} styles={{
                                                                    root: {
                                                                        float: "right",
                                                                        height: 32,
                                                                        marginTop: "-8px",
                                                                        border: `1px solid ${branding.theme.semanticColors.buttonBorder}`,
                                                                    }
                                                                }} iconProps={{iconName: "MailCheck"}}
                                                                                  text={t('Common.Button.PreviewEmail', {ns: 'common'})}
                                                                                  disabled={isSaving}
                                                                />

                                                                <SingleRHFRichTextControl
                                                                    id={"EmailSubject"}
                                                                    label={t('label.ctrlEmailSubject', {ns: 'common'})}
                                                                    placeholder={t('label.ctrlEmailSubject', {ns: 'common'})}
                                                                    setRteValue={setRteValueSubject}
                                                                    rteValue={rteValueSubject}
                                                                    control={myForm.control}
                                                                    setValue={myForm.setValue}
                                                                    getValues={myForm.getValues}
                                                                    trigger={myForm.trigger}
                                                                    rules={{required: t('text.fieldRequired', {ns: 'common'})}}
                                                                    onRenderOption={onRenderOption}
                                                                    options={templateContentControls}
                                                                    toolbarConfig={toolbarConfigEmailSubject}
                                                                    disabled={isSaving}
                                                                    isTextArea={true}
                                                                />
                                                            </StackItem>
                                                            <StackItem styles={{root: {paddingTop: 40}}}>

                                                                <CommandBarButton onClick={() => {
                                                                    ShowPreview()
                                                                }} styles={{
                                                                    root: {
                                                                        float: "right",
                                                                        height: 32,
                                                                        marginTop: "-8px",
                                                                        border: `1px solid ${branding.theme.semanticColors.buttonBorder}`,
                                                                    }
                                                                }} iconProps={{iconName: "MailCheck"}}
                                                                                  text={t('Common.Button.PreviewEmail', {ns: 'common'})}
                                                                                  disabled={isSaving}/>

                                                                <SingleRHFRichTextControl
                                                                    id={"EmailTemplate"}
                                                                    label={t('label.ctrlEmailBody', {ns: 'common'})}
                                                                    placeholder={t('label.ctrlEmailBody', {ns: 'common'})}
                                                                    setRteValue={setRteValue}
                                                                    rteValue={rteValue}
                                                                    control={myForm.control}
                                                                    setValue={myForm.setValue}
                                                                    getValues={myForm.getValues}
                                                                    trigger={myForm.trigger}
                                                                    onRenderOption={onRenderOption}
                                                                    options={templateContentControls}
                                                                    toolbarConfig={toolbarConfigEmailBody}
                                                                    disabled={isSaving}
                                                                />
                                                            </StackItem>


                                                        </>
                                                    }
                                                </form>
                                            </FormProvider>


                                        </>
                                    }


                                </Stack>
                                {/*-- END CONTENT --*/}

                                {/*-- START FOOTER --*/}
                                <Separator></Separator>
                                <Stack horizontal tokens={{childrenGap: 8}}
                                       styles={{root: {paddingRight: 16, paddingBottom: 12, alignItems: 'center'}}}>

                                    <Stack grow={1}>
                                        {" "}
                                    </Stack>
                                    <Stack>

                                        {action !== null && <>
                                            <PrimaryButton disabled={!isLoadedData || isSaving || isLoadingData}
                                                           style={{marginLeft: 8}}
                                                           onClick={() => {
                                                               onUpdate().then()
                                                           }}
                                                           text={t('Common.Button.Update', {ns: 'common'})}
                                                           title={t('Common.Button.Update', {ns: 'common'})}/>
                                        </>
                                        }
                                        {action === null &&
                                            <PrimaryButton disabled={!isLoadedData || isSaving || isLoadingData}
                                                           style={{marginLeft: 8}}
                                                           onClick={() => {
                                                               onSave().then()
                                                           }}
                                                           text={t('Common.Button.Save', {ns: 'common'})}
                                                           title={t('Common.Button.Save', {ns: 'common'})}/>
                                        }
                                    </Stack>

                                    <DefaultButton
                                        onClick={() => {
                                            onBeforeDismissAction()
                                        }}
                                        styles={{root: {marginRight: 8}}}
                                        text={t('Common.Button.Close', {ns: 'common'})}
                                        title={t('Common.Button.Close', {ns: 'common'})}/>

                                </Stack>
                                {/*-- END FOOTER --*/}

                            </Stack>

                        </Stack>
                    </Stack>
                </Stack>

            </Modal>

            {showEmailPreview &&
                <ApprovalEmailPreview
                    showModal={showEmailPreview}
                    action={currentAction}
                    onDismissModal={onDismissPreviewEmail}/>
            }

            {showLookup &&
                <ApprovalLookup
                    masterLists={masterLists}
                    showModal={showLookup}
                    templateContentControls={mapContentControls}
                    action={action}
                    onDismissModal={onDismissLookup}
                />
            }

        </>
    )
}


